import styled from '@emotion/styled';
import type {
  BrandInsight,
  FlavorCategoryInsight,
  FlavorCategoryInsightRankingsInner
} from '@innovationdepartment/proxima-sdk-axios';
import {
  type DatePickerProps,
  MagicStars,
  type SelectOption
} from '@innovationdepartment/proxima-ui';
import { brandCategoryOptions } from 'constants/brandCategories';
import { META_BENCHMARKS_HELP_GUIDE_URL } from 'constants/urls';
import * as dateFns from 'date-fns';
import { useProximaSDK, useShowSpinner } from 'hooks';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useAdAccountStore, useBrandStore } from 'stores';
import { InsightsInterval, InsightsMetric } from 'types/insights';
import type { MetaAdAccount } from 'types/stores/ad-account-store';
import { formatDateToIsoWithoutTime } from 'utils/formatDate';
import { checkFlavorCategoryValue } from '../insightsHelpers';
import { InsightsHeader } from '../SharedLayout';
import {
  InsightsNoMetaIntegration,
  InsightsSubheader,
  InsightsTraditionalBenchmarks
} from './Layout';

type QueryStringParams = {
  key: string;
  value: InsightsMetric | InsightsInterval | string;
};

const metricOptions = [
  {
    label: 'ROAS',
    value: InsightsMetric.Roas,
    icon: <MagicStars />
  },
  {
    label: 'CTR',
    value: InsightsMetric.Ctr,
    icon: <MagicStars />
  },
  {
    label: 'CPM',
    value: InsightsMetric.Cpm,
    icon: <MagicStars />
  },
  {
    label: 'CPA',
    value: InsightsMetric.Cpa,
    icon: <MagicStars />
  },
  {
    label: 'CPC',
    value: InsightsMetric.Cpc,
    icon: <MagicStars />
  },
  {
    label: 'CVR',
    value: InsightsMetric.Cvr,
    icon: <MagicStars />
  },
  {
    label: 'Spend',
    value: InsightsMetric.Spend,
    icon: <MagicStars />
  }
];

const intervalOptions = [
  {
    label: 'By Day',
    value: InsightsInterval.Day
  },
  {
    label: 'By Week',
    value: InsightsInterval.Week
  },
  {
    label: 'By Month',
    value: InsightsInterval.Month
  }
];

const ChartSection = styled.section`
  display: inline-block;
`;

const InsightsContainer = styled.div`
  background: linear-gradient(105deg, #e9fff9 0%, #e6edfe 100%);
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const InsightsInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InsightsStickyTop = styled.div`
  position: sticky;
  top: 0;
  z-index: 3;
`;

const Insights = () => {
  const [searchParams] = useSearchParams();
  const insightsApi = useProximaSDK('IntelligenceApi');
  const { metaAdAccountsList } = useAdAccountStore();

  const today = dateFns.startOfToday();
  const ninetyDaysAgo = dateFns.sub(today, { days: 90 }).toISOString();
  const endOfYesterday = dateFns.startOfDay(dateFns.endOfYesterday()).toISOString();

  const startDateFromSearchParams = dateFns.parseISO(
    searchParams?.get('startDate') || ninetyDaysAgo
  );
  const endDateFromSearchParams = dateFns.parseISO(searchParams?.get('endDate') || endOfYesterday);

  // metrics toggle for each chart
  const [traditionalBenchmarksMetric, setTraditionalBenchmarksMetric] = useState<InsightsMetric>(
    InsightsMetric.Roas
  );
  // const [performanceDifferencesMetric, setPerformanceDifferencesMetric] = useState<InsightsMetric>(
  //   InsightsMetric.Cpa,
  // );

  // states for the query string params
  // const [anchorMetric, setAnchorMetric] = useState<InsightsMetric>(
  //   (searchParams.get('anchorMetric') as InsightsMetric) || InsightsMetric.Cpa,
  // );
  const [interval, setInterval] = useState<InsightsInterval>(
    (searchParams.get('interval') as InsightsInterval) || InsightsInterval.Week
  );
  const [dateRange, setDateRange] = useState({
    startDate: startDateFromSearchParams,
    endDate: endDateFromSearchParams
  });

  const paramMetaAdAccountId = searchParams.get('adAccountId');
  const metaAdAccountFromSearchParams = metaAdAccountsList.find(
    account => account.adAccountId === paramMetaAdAccountId
  );

  const defaultMetaAdAccount = metaAdAccountFromSearchParams ?? metaAdAccountsList.at(0) ?? null;
  const [metaAdAccount, setMetaAdAccount] = useState<MetaAdAccount | null>(
    defaultMetaAdAccount ?? null
  );

  // states for the API calls
  const [flavorCategory, setFlavorCategory] = useState<string>(searchParams.get('category') || '');

  const [tradBenchmarksFlavorCatInsights, setTradBenchmarksFlavorCatInsights] = useState<
    FlavorCategoryInsight[]
  >([]);
  const [tradBenchmarksBrandInsights, setTradBenchmarksBrandInsights] = useState<BrandInsight[]>(
    []
  );

  // const [perfDiffFlavorCatInsights, setPerfDiffFlavorCatInsights] = useState<
  //   FlavorCatInsightsResponse[]
  // >([]);
  // const [perfDiffBrandInsights, setPerfDiffBrandInsights] = useState<BrandInsightsResponse[]>([]);

  const { brand } = useBrandStore();

  const updateQueryStringParams = ({ key, value }: QueryStringParams) => {
    // Query string has to be updated like this because setSearchParams causes an unnecessary page re-render and seems to be a known bug: https://github.com/remix-run/react-router/discussions/9851
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    window.history.pushState({}, '', url.toString());
  };

  // This is to fill in any missing values in the brand data for dates we have flavor category data for
  const fillBrandData = (
    brandInsights: BrandInsight[],
    flavorCategoryInsights: FlavorCategoryInsight[]
  ) => {
    const brandDates = new Map();

    brandInsights.forEach(brandData => {
      brandDates.set(brandData.date, brandData);
    });

    return flavorCategoryInsights.map(flavorData => {
      if (brandDates.has(flavorData.date)) return brandDates.get(flavorData.date);
      return { date: flavorData.date, value: 0 };
    });
  };

  const fetchTradBenchmarksData = async () => {
    // get brand insights
    if (metaAdAccount) {
      const brandInsightsResponse = await insightsApi.getBrandInsights({
        brandId: brand.brandId,
        adAccountId: metaAdAccount.adAccountId,
        metric: traditionalBenchmarksMetric,
        interval,
        endDate: formatDateToIsoWithoutTime(dateRange.endDate),
        startDate: formatDateToIsoWithoutTime(dateRange.startDate)
      });
      const updatedBrandInsights = brandInsightsResponse.data;

      // This is how we make sure the chart displays 2 decimal points
      const roundedBrandInsights = updatedBrandInsights.map((brandInsight: BrandInsight) => {
        let brandInsightValue = brandInsight.value || 0;

        // CTR gets multiplied by 100 because the backend value returns as a decimal instead of the percentage
        if (traditionalBenchmarksMetric === InsightsMetric.Ctr) brandInsightValue *= 100;

        return {
          ...brandInsight,
          value: Number(brandInsightValue.toFixed(2))
        };
      });

      setTradBenchmarksBrandInsights(roundedBrandInsights);

      // get flavor category insights
      if (flavorCategory) {
        const flavorCatResponse = await insightsApi.getFlavorCategoryInsights({
          flavorCategoryId: checkFlavorCategoryValue(flavorCategory),
          metric: traditionalBenchmarksMetric,
          anchorMetric: traditionalBenchmarksMetric,
          interval,
          endDate: formatDateToIsoWithoutTime(dateRange.endDate),
          startDate: formatDateToIsoWithoutTime(dateRange.startDate)
        });
        let updatedFlavorCatInsights = flavorCatResponse.data;

        // CTR gets multiplied by 100 because the backend value returns as a decimal instead of the percentage
        if (traditionalBenchmarksMetric === InsightsMetric.Ctr)
          updatedFlavorCatInsights = flavorCatResponse.data.map(
            (insight: FlavorCategoryInsight) => ({
              date: insight.date,
              rankings: insight.rankings?.map((ranking: FlavorCategoryInsightRankingsInner) => ({
                name: ranking.name,
                value: (ranking.value ?? 0) * 100
              }))
            })
          );

        const roundedFlavorCat = updatedFlavorCatInsights.map(
          (flavorCat: FlavorCategoryInsight) => ({
            ...flavorCat,
            rankings: flavorCat?.rankings?.map((ranking: FlavorCategoryInsightRankingsInner) => ({
              ...ranking,
              value: Number(ranking?.value?.toFixed(2))
            }))
          })
        );

        setTradBenchmarksFlavorCatInsights(roundedFlavorCat);

        if (roundedBrandInsights.length !== updatedFlavorCatInsights.length) {
          const filledTradBenchmarksBrandData = fillBrandData(
            roundedBrandInsights,
            updatedFlavorCatInsights
          );

          setTradBenchmarksBrandInsights(filledTradBenchmarksBrandData);
        }
      }
    }
  };

  // const fetchPerfDiffData = async () => {
  //   // get brand insights
  //   if (adAccount) {
  //     const brandInsightsResponse = await getBrandInsights({
  //       brandId: brand.brandId,
  //       adAccountId: adAccount!.accountId,
  //       metric: performanceDifferencesMetric,
  //       interval,
  //       endDate: formatDateToIsoWithoutTime(dateRange.endDate),
  //       startDate: formatDateToIsoWithoutTime(dateRange.startDate),
  //     });
  //     const updatedBrandInsights = brandInsightsResponse.data;

  //     setPerfDiffBrandInsights(updatedBrandInsights);

  //     // get flavor category insights
  //     if (flavorCategory) {
  //       const flavorCatResponse = await getFlavorCatInsights({
  //         flavorCategoryId: flavorCategory!.id,
  //         metric: performanceDifferencesMetric,
  //         anchorMetric,
  //         interval,
  //         endDate: formatDateToIsoWithoutTime(dateRange.endDate),
  //         startDate: formatDateToIsoWithoutTime(dateRange.startDate),
  //       });
  //       const updatedFlavorCatInsights = flavorCatResponse.data;

  //       setPerfDiffFlavorCatInsights(updatedFlavorCatInsights);

  //       if (updatedBrandInsights.length !== updatedFlavorCatInsights.length) {
  //         const filledFlavorCatBrandData = fillBrandData(
  //           perfDiffBrandInsights,
  //           updatedFlavorCatInsights,
  //         );
  //         setPerfDiffBrandInsights(filledFlavorCatBrandData!);
  //       }
  //     }
  //   }
  // };

  // const onAnchorMetricChanged = (newAnchorMetric: InsightsMetric) => {
  //   setAnchorMetric(newAnchorMetric);
  //   updateQueryStringParams({ key: 'anchorMetric', value: newAnchorMetric });
  // };

  const onIntervalChanged = (newInterval: InsightsInterval) => {
    setInterval(newInterval);
    updateQueryStringParams({ key: 'interval', value: newInterval });
  };

  const onDateRangeSelect = (newDateRange: DatePickerProps['dateRange']) => {
    const { startDate, endDate } = newDateRange;
    if (!startDate || !endDate) return;

    setDateRange({ startDate, endDate });
    updateQueryStringParams({
      key: 'startDate',
      value: formatDateToIsoWithoutTime(startDate)
    });
    updateQueryStringParams({
      key: 'endDate',
      value: formatDateToIsoWithoutTime(endDate)
    });
  };

  const onCategorySelect = (newCategory: string) => {
    setFlavorCategory(newCategory);
    updateQueryStringParams({ key: 'category', value: newCategory });
  };

  const traditionalBenchmarksHighchartOptions = {
    brandInsights: tradBenchmarksBrandInsights,
    flavorCatInsights: tradBenchmarksFlavorCatInsights
  };
  // const performanceDifferencesHighchartOptions = {
  //   brandInsights: perfDiffBrandInsights,
  //   flavorCatInsights: perfDiffFlavorCatInsights,
  // };

  // const anchorOptionLabel = metricOptions.find((option) => option.value === anchorMetric)?.label!;

  const onAdAccountSelect = (adAccountId: string) => {
    const adAccount = metaAdAccountsList.find(account => account.adAccountId === adAccountId);
    if (!adAccount) return;

    updateQueryStringParams({ key: 'adAccountId', value: adAccountId });
    setMetaAdAccount(adAccount);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    if (brand.category) setFlavorCategory(brand.category);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    fetchTradBenchmarksData();
  }, [flavorCategory, traditionalBenchmarksMetric, interval, dateRange, metaAdAccount]);

  // useEffect(() => {
  //   fetchPerfDiffData();
  // }, [flavorCategory, performanceDifferencesMetric, anchorMetric, interval, dateRange]);

  const categoryOptions: SelectOption[] = [
    {
      label: 'All Categories',
      value: 'all'
    },
    ...brandCategoryOptions
  ];

  useShowSpinner({ show: insightsApi.loading });

  return (
    <InsightsContainer>
      {!metaAdAccount && <InsightsNoMetaIntegration />}
      <InsightsStickyTop>
        <InsightsHeader
          brandName={brand.name}
          helpLink={META_BENCHMARKS_HELP_GUIDE_URL}
          title="Benchmarks | Meta"
        />
        <InsightsSubheader
          metaAdAccount={metaAdAccount}
          adAccounts={metaAdAccountsList}
          onAdAccountSelect={onAdAccountSelect}
          category={flavorCategory || ''}
          dateRange={dateRange}
          categoryOptions={categoryOptions}
          intervalOptions={intervalOptions}
          interval={interval}
          onCategorySelect={onCategorySelect}
          onDateRangeSelect={onDateRangeSelect}
          onIntervalChanged={onIntervalChanged}
        />
      </InsightsStickyTop>
      <InsightsInnerContainer>
        <ChartSection>
          <InsightsTraditionalBenchmarks
            currentMetric={traditionalBenchmarksMetric}
            metricsOptions={metricOptions}
            chartOptions={traditionalBenchmarksHighchartOptions}
            onMetricChanged={setTraditionalBenchmarksMetric}
          />
        </ChartSection>
        {/* TODO (Milecia): leaving this commented out because we'll likely add this back at some point. */}
        {/* <ChartSection>
          <InsightsPerformanceDifferences
            currentMetric={performanceDifferencesMetric}
            anchorMetric={anchorOptionLabel}
            metricsOptions={metricOptions}
            chartOptions={performanceDifferencesHighchartOptions}
            onMetricChanged={setPerformanceDifferencesMetric}
          />
        </ChartSection> */}
      </InsightsInnerContainer>
    </InsightsContainer>
  );
};

export default Insights;
